import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { TicketFilterAnfrage, TicketKarte, Unternehmen } from "@kundeninfo/api-client";
import { Auswahl, Auswahlmenue, Knopf, Optionen, Werkzeuggruppe } from "@kundeninfo/ui/me-design";
import { personOption } from "./Avatar.js";
import type { AvatarPerson } from "./Avatar.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DIE FILTER DES BOARDS (Spec „Board“ 115, F35, F129) — Unternehmen, Kunde, Zuständig (me-auswahlmenue
 * mit Avatar je Option und im Auslöser, F48/Entscheid 53) und „nur verknüpfte“ als Umschalt-Chip
 * (`me-knopf gestalt="pille"` mit `gedrueckt`, 61d). Board und Liste teilen sie (Spec „Liste“, Ruling A4)
 * samt Gedächtnis (`boardSpeicher.ts`). Gibt die vier Gruppen (`me-werkzeuggruppe`, Name als
 * zugänglicher Name und Sofort-Popup, `aktiv`, wenn der Filter von der Vorgabe abweicht) für den Slot
 * `filter` der Werkzeugleiste zurück; die Leiste drumherum gehört der Ansicht. Kunden kommen aus den
 * gezeigten Tickets, Personen aus `listTicketAssignees` (Rückfall: die Zuständigen der Tickets).
 */

export function filterAnfrage(f: BoardFilter): TicketFilterAnfrage {
  return {
    ...(f.company !== "ALLE" ? { company: f.company } : {}),
    ...(f.customer !== "" ? { customer: f.customer } : {}),
    ...(f.assignee !== "" ? { assignee: f.assignee } : {}),
    ...(f.linked ? { linked: true } : {}),
  };
}

/** Wie viele der vier Filter von der Vorgabe abweichen — der Zähler an „Filter (n)“ und die Bedingung
 *  für „Zurücksetzen“ der Werkzeugleiste (F129, 61f, 61l). */
export function anzahlAktiverFilter(f: BoardFilter): number {
  return [f.company !== "ALLE", f.customer !== "", f.assignee !== "", f.linked].filter((x) => x).length;
}

export type TicketFilterleisteProps = {
  client: Pick<TicketsClient, "listTicketAssignees">; companies: readonly Unternehmen[];
  /** Die eigene Person für „Ich“; ohne sie entfällt die Wahl. */
  ich: { id: string; name: string } | null;
  tickets: readonly TicketKarte[]; filter: BoardFilter; onFilter: (f: Partial<BoardFilter>) => void;
};

export function TicketFilterleiste(p: TicketFilterleisteProps): ReactElement {
  // Der Name des gewählten Kunden nur im Speicher der Sitzung (nie im Browserspeicher).
  const [kundeName, setKundeName] = useState("");
  const [personen, setPersonen] = useState<readonly AvatarPerson[]>([]);
  const ichId = p.ich?.id ?? null;

  // Wer zuständig sein kann (mit Mail für den Steckbrief, F48) — einmal beim Öffnen; ohne Antwort
  // bleiben die Zuständigen der Tickets.
  useEffect(() => {
    let aktiv = true;
    p.client.listTicketAssignees()
      .then((r) => { if (aktiv) setPersonen(r.items); })
      .catch(() => { /* Rückfall: die Zuständigen der Tickets */ });
    return () => { aktiv = false; };
  }, [p.client]);

  const kunden = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of p.tickets) for (const c of t.customers) m.set(c.id, c.name);
    if (p.filter.customer !== "" && !m.has(p.filter.customer)) {
      m.set(p.filter.customer, kundeName !== "" ? kundeName : "gewählter Kunde");
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [p.tickets, p.filter.customer, kundeName]);
  const zustaendige = useMemo(() => {
    const m = new Map<string, AvatarPerson>();
    for (const t of p.tickets) if (t.assignee !== null) m.set(t.assignee.id, t.assignee);
    for (const u of personen) m.set(u.id, u);
    if (p.filter.assignee !== "" && p.filter.assignee !== "none" && !m.has(p.filter.assignee)) {
      m.set(p.filter.assignee, { id: p.filter.assignee, name: "gewählte Person" });
    }
    if (ichId !== null) m.delete(ichId);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [p.tickets, personen, p.filter.assignee, ichId]);
  const ichPerson: AvatarPerson | null = useMemo(() => {
    if (p.ich === null) return null;
    return personen.find((u) => u.id === p.ich!.id) ?? { id: p.ich.id, name: p.ich.name };
  }, [personen, p.ich]);

  return (
    <>
      <Werkzeuggruppe beschriftung="Unternehmen" aktiv={p.filter.company !== "ALLE"}>
        <Optionen aria-label="Unternehmen" darstellung="segmente" richtung="waagerecht" wert={p.filter.company}
          onGeaendert={(d) => p.onFilter({ company: d.wert ?? "ALLE" })}>
          <option value="ALLE">Alle</option>
          {p.companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode}</option>)}
        </Optionen>
      </Werkzeuggruppe>
      <Werkzeuggruppe beschriftung="Kunde" aktiv={p.filter.customer !== ""}>
        {/* Höchstbreite 10 em (Tickets.css `.tf__kunde`, Übergang T-13): Ein langer Kundenname soll die
            Leiste nicht in eine tiefere Stufe drücken (Befund E2E 1d-B); der Name wird im Feld abgeschnitten. */}
        <Auswahl kompakt className="tf__kunde" beschriftung="Kunde" wert={p.filter.customer}
          onGeaendert={(d) => {
            setKundeName(kunden.find(([id]) => id === d.wert)?.[1] ?? "");
            p.onFilter({ customer: d.wert });
          }}>
          <option value="">Alle Kunden</option>
          {kunden.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Auswahl>
      </Werkzeuggruppe>
      {/* Die Werte bleiben die des Filtermodells ("" alle, Kennung, "none"); die Sonderoptionen
          tragen data-art, damit das Element sie oben mit Gruppensymbol, eigenem Avatar bzw.
          gestricheltem Kreis zeigt und von der Suche ausnimmt. */}
      {/* Höchstbreite 10 em am Auslöser (Tickets.css `.tf__zustaendig`, Übergang T-13), wie beim Kunden. */}
      <Werkzeuggruppe beschriftung="Zuständig" aktiv={p.filter.assignee !== ""}>
        <Auswahlmenue kompakt className="tf__zustaendig" beschriftung="Zuständig" wert={p.filter.assignee}
          onGeaendert={(d) => p.onFilter({
            assignee: d.art === "alle" ? "" : d.art === "niemand" ? "none" : d.wert })}>
          <option value="" data-art="alle">Alle</option>
          {ichPerson !== null && <option value={ichPerson.id} data-art="ich" {...personOption(ichPerson)}>Ich</option>}
          <option value="none" data-art="niemand">Niemand</option>
          {zustaendige.map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
        </Auswahlmenue>
      </Werkzeuggruppe>
      <Werkzeuggruppe beschriftung="Verknüpfung" aktiv={p.filter.linked}>
        <Knopf art="sekundaer" gestalt="pille" gedrueckt={p.filter.linked}
          onClick={() => p.onFilter({ linked: !p.filter.linked })}>
          nur verknüpfte
        </Knopf>
      </Werkzeuggruppe>
    </>
  );
}
